import React, { useEffect, useState } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Shield, 
  LogOut, 
  Newspaper, 
  Users, 
  Heart, 
  BarChart3,
  Menu,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import NewsManagement from './NewsManagement';
import LeadershipManagement from './LeadershipManagement';
import ValuesManagement from './ValuesManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    newsArticles: 0,
    leadershipMembers: 0,
    coreValues: 0
  });

  useEffect(() => {
    checkAuth();
    loadStats();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error('Authentication failed:', error);
      navigate('/admin/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Load stats with published_only=false for admin view
      const [newsResponse, leadershipResponse, valuesResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/news/?published_only=false`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/leadership/`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/values/`, { withCredentials: true })
      ]);

      setStats({
        newsArticles: newsResponse.data.length,
        leadershipMembers: leadershipResponse.data.length,
        coreValues: valuesResponse.data.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
      // If not authenticated, try loading public data for counts
      try {
        const [publicNewsResponse, publicLeadershipResponse, publicValuesResponse] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/news/`),
          axios.get(`${BACKEND_URL}/api/leadership/`),
          axios.get(`${BACKEND_URL}/api/values/`)
        ]);

        setStats({
          newsArticles: publicNewsResponse.data.length,
          leadershipMembers: publicLeadershipResponse.data.length,
          coreValues: publicValuesResponse.data.length
        });
      } catch (fallbackError) {
        console.error('Error loading public stats:', fallbackError);
        // Set default values if API calls fail
        setStats({
          newsArticles: 2, // Known seeded data
          leadershipMembers: 2, // Keyvin & Rey
          coreValues: 2 // Innovation & Integrity
        });
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      toast.success('Logged out successfully');
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Error logging out');
    }
  };

  const navigationItems = [
    { 
      id: 'overview',
      title: 'Overview', 
      icon: <BarChart3 className="h-5 w-5" />,
      path: '/admin/dashboard'
    },
    { 
      id: 'news',
      title: 'News Articles', 
      icon: <Newspaper className="h-5 w-5" />,
      path: '/admin/dashboard/news'
    },
    { 
      id: 'leadership',
      title: 'Leadership', 
      icon: <Users className="h-5 w-5" />,
      path: '/admin/dashboard/leadership'
    },
    { 
      id: 'values',
      title: 'Core Values', 
      icon: <Heart className="h-5 w-5" />,
      path: '/admin/dashboard/values'
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const isOverview = location.pathname === '/admin/dashboard';

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-vincotek-dark transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <img 
              src="/assets/Logo%20Vincotek%20Pty%20Ltd-White.png" 
              alt="Vincotek Logo" 
              className="h-8 w-auto"
            />
            <span className="text-xl font-bold text-white">Admin</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="mt-8 px-4">
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-vincotek-yellow text-vincotek-dark'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3">{item.title}</span>
                </Link>
              );
            })}
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 bg-vincotek-yellow rounded-full flex items-center justify-center">
                  <span className="text-vincotek-dark font-semibold text-sm">
                    {user?.name?.charAt(0)}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-gray-600 text-gray-300 hover:bg-red-600 hover:text-white hover:border-red-600"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-bold text-vincotek-dark">
                {isOverview ? 'Dashboard Overview' : 
                 location.pathname.includes('/news') ? 'News Management' :
                 location.pathname.includes('/leadership') ? 'Leadership Management' :
                 location.pathname.includes('/values') ? 'Core Values Management' : 'Dashboard'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  View Website
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="p-6">
          <Routes>
            <Route path="/" element={
              <div className="space-y-6">
                {/* Stats Cards - Clickable */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Link to="/admin/dashboard/news">
                    <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">News Articles</p>
                            <p className="text-3xl font-bold text-vincotek-dark group-hover:text-blue-600 transition-colors">{stats.newsArticles}</p>
                            <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                          </div>
                          <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                            <Newspaper className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  
                  <Link to="/admin/dashboard/leadership">
                    <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Leadership Members</p>
                            <p className="text-3xl font-bold text-vincotek-dark group-hover:text-green-600 transition-colors">{stats.leadershipMembers}</p>
                            <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                          </div>
                          <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center group-hover:bg-green-100 transition-colors">
                            <Users className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                  
                  <Link to="/admin/dashboard/values">
                    <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 group">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-600">Core Values</p>
                            <p className="text-3xl font-bold text-vincotek-dark group-hover:text-yellow-600 transition-colors">{stats.coreValues}</p>
                            <p className="text-xs text-gray-500 mt-1">Click to manage</p>
                          </div>
                          <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center group-hover:bg-yellow-100 transition-colors">
                            <Heart className="h-6 w-6 text-yellow-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </div>

                {/* Welcome Card */}
                <Card className="bg-gradient-to-r from-vincotek-blue to-vincotek-dark text-white border-0">
                  <CardContent className="p-8">
                    <h2 className="text-2xl font-bold mb-2">
                      Welcome back, {user?.name?.split(' ')[0]}!
                    </h2>
                    <p className="text-blue-100 mb-6">
                      Manage your Vincotek website content from this centralized dashboard.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Link to="/admin/dashboard/news">
                        <Button className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30">
                          Manage News
                        </Button>
                      </Link>
                      <Link to="/admin/dashboard/leadership">
                        <Button className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30">
                          Manage Leadership
                        </Button>
                      </Link>
                      <Link to="/admin/dashboard/values">
                        <Button className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30">
                          Manage Values
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>
            } />
            <Route path="/news" element={<NewsManagement />} />
            <Route path="/leadership" element={<LeadershipManagement />} />
            <Route path="/values" element={<ValuesManagement />} />
          </Routes>
        </main>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden" 
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminDashboard;